import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client'

import BackHeader from '../../components/BackHeader/BackHeader'
import Button from '../../components/Button/Button'
import Card from '../../components/Card/Card'
import Loading from '../../components/Loading/Loading'
import Modal from '../../components/Modal/Modal'
import Notification from '../../components/Notification/Notification'
import Spacer from '../../components/Spacer/Spacer'
import PullToRefresh from '../../components/PullToRefresh/PullToRefresh'

import { KAMPDAG_GUESSES, KAMPDAG_MATCHES } from '../../graphql/queries'
import { UPSERT_MY_KAMPDAG_GUESS } from '../../graphql/mutations'

import './Kampdag.css'

const MATCH_DURATION_MS = 100 * 60 * 1000

const CATEGORY_DEFS = [
  {
    key: 'result',
    label: 'Resultat',
    enabledKey: 'enableResult',
  },
  {
    key: 'yellowCards',
    label: 'Gule kort',
    enabledKey: 'enableYellowCards',
    actualKey: 'yellowCards',
    guessKey: 'yellowCards',
    short: 'Gule',
  },
  {
    key: 'redCards',
    label: 'Røde kort',
    enabledKey: 'enableRedCards',
    actualKey: 'redCards',
    guessKey: 'redCards',
    short: 'Røde',
  },
  {
    key: 'corners',
    label: 'Hjørnespark',
    enabledKey: 'enableCorners',
    actualKey: 'corners',
    guessKey: 'corners',
    short: 'Hjørner',
  },
]

const fmtKickoff = (iso) => {
  const d = new Date(iso)
  return new Intl.DateTimeFormat('da-DK', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

const isLocked = (kickoffIso) => {
  const kickoff = new Date(kickoffIso).getTime()
  return Date.now() >= kickoff
}

const hasFinalResult = (match) => {
  if (!match) return false
  if (match.enableResult && (!Number.isInteger(match.homeScore) || !Number.isInteger(match.awayScore))) return false
  if (match.enableYellowCards && !Number.isInteger(match.yellowCards)) return false
  if (match.enableRedCards && !Number.isInteger(match.redCards)) return false
  if (match.enableCorners && !Number.isInteger(match.corners)) return false
  return true
}

const guessHitsCategory = (match, guess, def) => {
  if (!match || !guess) return false
  if (def.key === 'result') {
    if (!match.enableResult) return false
    if (!Number.isInteger(match.homeScore) || !Number.isInteger(match.awayScore)) return false
    return guess.homeScore === match.homeScore && guess.awayScore === match.awayScore
  }
  if (!match[def.enabledKey]) return false
  const actual = match[def.actualKey]
  const guessed = guess[def.guessKey]
  if (!Number.isInteger(actual) || !Number.isInteger(guessed)) return false
  return actual === guessed
}

const enabledCategoriesFor = (match) =>
  CATEGORY_DEFS.filter((def) => (def.key === 'result' ? match.enableResult : match[def.enabledKey]))

const categoryHasActual = (match, def) => {
  if (def.key === 'result') {
    return match.enableResult && Number.isInteger(match.homeScore) && Number.isInteger(match.awayScore)
  }
  return match[def.enabledKey] && Number.isInteger(match[def.actualKey])
}

const guessHasValueFor = (guess, def) => {
  if (def.key === 'result') {
    return Number.isInteger(guess?.homeScore) && Number.isInteger(guess?.awayScore)
  }
  return Number.isInteger(guess?.[def.guessKey])
}

const distanceFor = (match, guess, def) => {
  if (def.key === 'result') {
    return Math.abs(guess.homeScore - match.homeScore) + Math.abs(guess.awayScore - match.awayScore)
  }
  return Math.abs(guess[def.guessKey] - match[def.actualKey])
}

// Returner Map<guessId, { points, scored, distance }> for én kategori for alle gæt.
// 2 point for præcis korrekt (afstand 0), 1 point til alle der er tættest på (afstand > 0).
const scoreCategoryForAllGuesses = (match, guesses, def) => {
  const result = new Map()
  const hasActual = categoryHasActual(match, def)

  for (const g of guesses) {
    result.set(g.id, { points: 0, scored: hasActual, distance: null })
  }

  if (!hasActual) return result

  const valid = guesses.filter((g) => guessHasValueFor(g, def))
  if (valid.length === 0) return result

  const distances = valid.map((g) => distanceFor(match, g, def))
  const minDist = Math.min(...distances)
  const points = minDist === 0 ? 2 : 1

  valid.forEach((g, i) => {
    const isWinner = distances[i] === minDist
    result.set(g.id, {
      points: isWinner ? points : 0,
      scored: true,
      distance: distances[i],
    })
  })

  return result
}

const computeLeaderboard = (match, guesses) => {
  if (!match || guesses.length === 0) {
    return { hasScoring: false, rows: [], leaders: [], leaderPoints: 0 }
  }
  const cats = enabledCategoriesFor(match)
  const perCatScores = {}
  let anyScored = false
  for (const def of cats) {
    const m = scoreCategoryForAllGuesses(match, guesses, def)
    perCatScores[def.key] = m
    for (const v of m.values()) {
      if (v.scored) anyScored = true
    }
  }

  const rows = guesses.map((g) => {
    const perCategory = {}
    let total = 0
    for (const def of cats) {
      const r = perCatScores[def.key].get(g.id) || { points: 0, scored: false, distance: null }
      perCategory[def.key] = r
      total += r.points
    }
    return { guess: g, perCategory, totalPoints: total }
  })

  if (!anyScored) return { hasScoring: false, rows, leaders: [], leaderPoints: 0 }

  const sorted = [...rows].sort((a, b) => b.totalPoints - a.totalPoints)
  const leaderPoints = sorted[0]?.totalPoints || 0
  const leaders = sorted.filter((r) => r.totalPoints === leaderPoints && r.totalPoints > 0)
  return { hasScoring: true, rows: sorted, leaders, leaderPoints }
}

function Kampdag() {
  const navigate = useNavigate()
  const user = useSelector((state) => state.mate.currentUser)
  const mateId = user?.id

  const [selectedMatchId, setSelectedMatchId] = useState(null)
  const [showGuesses, setShowGuesses] = useState(false)
  const [guessDialogOpen, setGuessDialogOpen] = useState(false)
  const [guessForm, setGuessForm] = useState({
    homeScore: '',
    awayScore: '',
    yellowCards: '',
    redCards: '',
    corners: '',
  })
  const [toast, setToast] = useState({ open: false, message: '', type: 'info' })
  const [rulesOpen, setRulesOpen] = useState(false)
  const [nowTick, setNowTick] = useState(Date.now())
  const [refreshingMatchId, setRefreshingMatchId] = useState(null)
  const nextMatchRef = useRef(null)

  const { data: matchesData, loading: matchesLoading, error: matchesError, refetch: refetchMatches } = useQuery(
    KAMPDAG_MATCHES,
    {
      fetchPolicy: 'cache-and-network',
    }
  )

  const fixtures = useMemo(() => {
    const list = matchesData?.kampdagMatches
    return Array.isArray(list)
      ? [...list].sort((a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime())
      : []
  }, [matchesData])

  const { pastMatches, featuredMatch, displayOrder } = useMemo(() => {
    const now = nowTick
    let nextIdx = null
    for (let i = 0; i < fixtures.length; i++) {
      const kickoff = new Date(fixtures[i].kickoff).getTime()
      if (kickoff + MATCH_DURATION_MS > now) {
        nextIdx = i
        break
      }
    }

    if (nextIdx === null) {
      return { pastMatches: [...fixtures], featuredMatch: null, displayOrder: [...fixtures] }
    }
    const past = fixtures.slice(0, nextIdx)
    const next = fixtures[nextIdx]
    const upcoming = fixtures.slice(nextIdx + 1)
    return {
      pastMatches: past,
      featuredMatch: next,
      displayOrder: [...past, next, ...upcoming],
    }
  }, [fixtures, nowTick])

  useEffect(() => {
    const defaultId = featuredMatch?.id || displayOrder[0]?.id
    if (!defaultId) return
    if (!selectedMatchId) {
      setSelectedMatchId(defaultId)
      return
    }
    const stillExists = fixtures.some((m) => m.id === selectedMatchId)
    if (!stillExists) setSelectedMatchId(defaultId)
  }, [featuredMatch?.id, displayOrder, fixtures, selectedMatchId])

  const selectedMatch = useMemo(
    () => fixtures.find((m) => m.id === selectedMatchId) || null,
    [fixtures, selectedMatchId]
  )

  const {
    data: guessesData,
    loading: guessesLoading,
    error: guessesError,
    refetch: refetchGuesses,
  } = useQuery(KAMPDAG_GUESSES, {
    variables: { matchId: selectedMatchId },
    skip: !selectedMatchId,
    fetchPolicy: 'cache-and-network',
  })

  const matchGuesses = useMemo(() => {
    const list = guessesData?.kampdagGuesses
    return Array.isArray(list) ? list : []
  }, [guessesData])

  const myGuess = useMemo(() => {
    if (!mateId) return null
    return matchGuesses.find((g) => g.mateId === mateId) || null
  }, [matchGuesses, mateId])

  const [upsertMyGuess, { loading: savingGuess }] = useMutation(UPSERT_MY_KAMPDAG_GUESS, {
    onCompleted: () => {
      setToast({ open: true, message: 'Gæt gemt!', type: 'success' })
      setGuessDialogOpen(false)
      refetchGuesses()
    },
    onError: (e) => {
      setToast({ open: true, message: e.message || 'Kunne ikke gemme gæt.', type: 'error' })
    },
  })

  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (!showGuesses || !selectedMatchId) return
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(max-width: 768px)')
    const apply = () => {
      document.body.style.overflow = mq.matches ? 'hidden' : ''
    }
    apply()
    mq.addEventListener('change', apply)
    return () => {
      mq.removeEventListener('change', apply)
      document.body.style.overflow = ''
    }
  }, [showGuesses, selectedMatchId])

  useEffect(() => {
    if (!featuredMatch?.id || !nextMatchRef.current) return
    nextMatchRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [featuredMatch?.id])

  const formatCountdown = (kickoffIso) => {
    const kickoff = new Date(kickoffIso).getTime()
    const diff = kickoff - nowTick
    if (diff <= 0) return 'Starter nu'
    const totalSec = Math.floor(diff / 1000)
    const days = Math.floor(totalSec / 86400)
    const hours = Math.floor((totalSec % 86400) / 3600)
    const mins = Math.floor((totalSec % 3600) / 60)
    const secs = totalSec % 60
    if (days > 0) return `Starter om ${days}d ${hours}t ${mins}m`
    if (hours > 0) return `Starter om ${hours}t ${mins}m`
    if (mins > 0) return `Starter om ${mins}m ${secs}s`
    return `Starter om ${secs}s`
  }

  const getMatchStatus = (match) => {
    if (!match) return { label: '', live: false }
    const kickoff = new Date(match.kickoff).getTime()
    if (Number.isNaN(kickoff)) return { label: 'Ukendt', live: false }
    if (nowTick < kickoff) return { label: 'Ikke startet', live: false }
    const elapsed = nowTick - kickoff
    if (hasFinalResult(match) || elapsed >= MATCH_DURATION_MS) return { label: 'Overstået', live: false }
    return { label: 'I gang', live: true }
  }

  const openGuessDialog = (match) => {
    setSelectedMatchId(match.id)
    const existing = matchGuesses.find((g) => g.mateId === mateId)
    setGuessForm({
      homeScore: existing?.homeScore ?? '',
      awayScore: existing?.awayScore ?? '',
      yellowCards: existing?.yellowCards ?? '',
      redCards: existing?.redCards ?? '',
      corners: existing?.corners ?? '',
    })
    setGuessDialogOpen(true)
  }

  const closeGuessDialog = () => setGuessDialogOpen(false)
  const closeToast = () => setToast((p) => ({ ...p, open: false }))

  const clampInt = (v) => {
    if (v === '' || v === null || v === undefined) return ''
    const n = Number(v)
    if (Number.isNaN(n)) return ''
    return Math.max(0, Math.trunc(n))
  }

  const handleSaveGuess = () => {
    if (!selectedMatch || !mateId) return
    if (isLocked(selectedMatch.kickoff)) {
      setToast({ open: true, message: 'Kampen er startet, der kan ikke gættes mere på denne kamp.', type: 'warning' })
      return
    }

    const variables = {
      input: {
        matchId: selectedMatch.id,
      },
    }

    if (selectedMatch.enableResult) {
      const h = clampInt(guessForm.homeScore)
      const a = clampInt(guessForm.awayScore)
      if (h === '' || a === '') {
        setToast({ open: true, message: 'Udfyld både hjemme- og udemål.', type: 'error' })
        return
      }
      variables.input.homeScore = h
      variables.input.awayScore = a
    }
    if (selectedMatch.enableYellowCards) {
      const v = clampInt(guessForm.yellowCards)
      if (v === '') {
        setToast({ open: true, message: 'Udfyld antal gule kort.', type: 'error' })
        return
      }
      variables.input.yellowCards = v
    }
    if (selectedMatch.enableRedCards) {
      const v = clampInt(guessForm.redCards)
      if (v === '') {
        setToast({ open: true, message: 'Udfyld antal røde kort.', type: 'error' })
        return
      }
      variables.input.redCards = v
    }
    if (selectedMatch.enableCorners) {
      const v = clampInt(guessForm.corners)
      if (v === '') {
        setToast({ open: true, message: 'Udfyld antal hjørnespark.', type: 'error' })
        return
      }
      variables.input.corners = v
    }

    upsertMyGuess({ variables })
  }

  const handleMatchClick = (match) => {
    if (!match?.id) return
    if (selectedMatchId === match.id) {
      setShowGuesses((v) => !v)
      return
    }
    setSelectedMatchId(match.id)
    setShowGuesses(true)
  }

  const handleRefreshMatch = async (e, match) => {
    e?.stopPropagation?.()
    if (!match?.id || refreshingMatchId !== null) return
    setRefreshingMatchId(match.id)
    try {
      await refetchMatches()
      if (selectedMatchId === match.id) {
        await refetchGuesses()
      }
    } finally {
      setRefreshingMatchId(null)
    }
  }

  const renderActualValue = (match, def) => {
    if (def.key === 'result') {
      const h = Number.isInteger(match.homeScore) ? match.homeScore : '-'
      const a = Number.isInteger(match.awayScore) ? match.awayScore : '-'
      return `${h}-${a}`
    }
    const val = match[def.actualKey]
    return Number.isInteger(val) ? val : '-'
  }

  const renderGuessValue = (guess, def) => {
    if (def.key === 'result') {
      const h = Number.isInteger(guess.homeScore) ? guess.homeScore : '-'
      const a = Number.isInteger(guess.awayScore) ? guess.awayScore : '-'
      return `${h}-${a}`
    }
    const val = guess[def.guessKey]
    return Number.isInteger(val) ? val : '-'
  }

  const selectedLeaderboard = useMemo(
    () => computeLeaderboard(selectedMatch, matchGuesses),
    [selectedMatch, matchGuesses]
  )

  const medalFor = (rank) => {
    if (rank === 0) return '🥇'
    if (rank === 1) return '🥈'
    if (rank === 2) return '🥉'
    return ''
  }

  const refreshKampdag = async () => {
    await refetchMatches()
    if (selectedMatchId) await refetchGuesses()
  }

  return (
    <PullToRefresh onRefresh={refreshKampdag}>
    <div className="kampdag-page">
      <div className="kampdag-background"></div>

      <div className="kampdag-container">
        <BackHeader
          className="kampdag-bar"
          theme="kampdag"
          eyebrow="Svirr"
          title="Kampdag"
          onBack={() => navigate('/spil')}
          backLabel="Tilbage til spil"
          actions={(
            <button
              type="button"
              className="kampdag-rules-btn"
              onClick={() => setRulesOpen(true)}
            >
              Regler
            </button>
          )}
        />
        {matchesError ? <p className="kampdag-muted">Kunne ikke hente kampe lige nu.</p> : null}

        <div>
          <Notification
            open={toast.open}
            onClose={closeToast}
            message={toast.message}
            type={toast.type}
            autoHideDuration={3500}
          />

          {matchesLoading && displayOrder.length === 0 ? <p className="kampdag-muted">Henter kampdage...</p> : null}

          {displayOrder.length > 0 ? (
            <div
              className={`kampdag-matches-wrapper${showGuesses && selectedMatchId ? ' kampdag-matches-wrapper--mobile-fs-active' : ''}`}
            >
              <div className="kampdag-matches">
                {displayOrder.map((match) => {
                  const isFeatured = featuredMatch?.id && match.id === featuredMatch.id
                  const isPast = pastMatches.some((m) => m.id === match.id)
                  const isSelected = selectedMatchId === match.id
                  const isExpanded = showGuesses && isSelected
                  const kickoffTs = new Date(match.kickoff).getTime()
                  const { label: statusLabel, live } = getMatchStatus(match)
                  const enabledCats = enabledCategoriesFor(match)

                  return (
                    <div
                      key={match.id}
                      ref={isFeatured ? nextMatchRef : null}
                      className={`kampdag-clickable ${isPast ? 'kampdag-match--past' : ''} ${isFeatured ? 'kampdag-match--next' : ''} ${isExpanded ? 'kampdag-match-fullscreen' : ''}`}
                      onClick={() => handleMatchClick(match)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') handleMatchClick(match)
                      }}
                    >
                      {isExpanded ? (
                        <BackHeader
                          className="kampdag-mobile-fs-back"
                          theme="kampdag"
                          eyebrow="Kampdag"
                          title="Kampoversigt"
                          onBack={(event) => {
                            event.stopPropagation()
                            setShowGuesses(false)
                          }}
                          backLabel="Tilbage til kampoversigt"
                        />
                      ) : null}
                      <Card
                        padding={isFeatured ? 'medium' : 'small'}
                        className={`kampdag-match-card ${
                          isFeatured ? 'kampdag-match-card--featured' : 'kampdag-match-card--compact'
                        } ${isPast ? 'kampdag-match-card--past' : ''}`}
                        hover={false}
                      >
                        <div className="kampdag-card-top">
                          <div className="kampdag-kickoff">{fmtKickoff(match.kickoff)}</div>
                          <div className="kampdag-card-top-trailing">
                            <div className="kampdag-card-chips">
                              {isFeatured ? <span className="kampdag-chip next">Næste kamp</span> : null}
                              <span className={`kampdag-chip live ${live ? 'kampdag-chip--live-icon' : ''}`}>
                                {live ? (
                                  <>
                                    <span className="kampdag-football-icon" aria-hidden="true">⚽</span>
                                    {statusLabel}
                                  </>
                                ) : (
                                  statusLabel
                                )}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="leaderboard-refresh-button kampdag-match-refresh-button"
                              onClick={(e) => handleRefreshMatch(e, match)}
                              disabled={refreshingMatchId !== null}
                              title="Hent seneste kampdata og gæt"
                              aria-label="Opdater kamp"
                            >
                              {refreshingMatchId === match.id ? (
                                <Loading size="small" variant="refresh" inline text="" />
                              ) : (
                                <span className="leaderboard-refresh-button-icon">
                                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                    <path
                                      d="M21 12a9 9 0 1 1-3.3-6.9"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <polyline
                                      points="21 3 21 9 15 9"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </span>
                              )}
                            </button>
                          </div>
                        </div>

                        {match.title ? <div className="kampdag-match-title">{match.title}</div> : null}

                        <div className="kampdag-details-title">
                          {match.homeTeam} <span className="kampdag-vs">vs</span> {match.awayTeam}
                        </div>
                        {(match.venue || match.city) ? (
                          <div className="kampdag-meta">
                            {match.venue}{match.city ? `, ${match.city}` : ''}
                          </div>
                        ) : null}
                        <div className="kampdag-statusline">
                          {nowTick < kickoffTs ? formatCountdown(match.kickoff) : `Status: ${statusLabel}`}
                        </div>

                        <div className="kampdag-categories">
                          {enabledCats.map((def) => (
                            <div key={def.key} className="kampdag-category">
                              <div className="kampdag-category__label">{def.label}</div>
                              <div className="kampdag-category__value">{renderActualValue(match, def)}</div>
                            </div>
                          ))}
                        </div>

                        {!isLocked(match.kickoff) && (
                          <div className="kampdag-actions" onClick={(e) => e.stopPropagation()}>
                            <Button
                              title={isSelected && myGuess ? 'Ret mit gæt' : 'Gæt'}
                              onClick={() => openGuessDialog(match)}
                              color="secondary"
                            />
                          </div>
                        )}

                        {isSelected && selectedLeaderboard.hasScoring && selectedLeaderboard.leaders.length > 0 && (
                          <div className="kampdag-winner-banner" onClick={(e) => e.stopPropagation()}>
                            <span className="kampdag-winner-banner__medal" aria-hidden="true">🏆</span>
                            <span className="kampdag-winner-banner__text">
                              {selectedLeaderboard.leaders.length === 1 ? 'Vinder' : 'Førende'}:{' '}
                              <strong>
                                {selectedLeaderboard.leaders.map((r) => r.guess.mateName || 'Ukendt').join(', ')}
                              </strong>{' '}
                              · {selectedLeaderboard.leaderPoints} point
                            </span>
                          </div>
                        )}

                        {isExpanded && (
                          <div className="kampdag-guesses" onClick={(e) => e.stopPropagation()}>
                            <div className="kampdag-guesses__header">
                              <strong>Alle gæt på denne kampdag</strong>
                              {selectedLeaderboard.hasScoring ? (
                                <span className="kampdag-scoring-info">Sorteret efter point</span>
                              ) : null}
                            </div>

                            {guessesError ? <div className="kampdag-muted">Kunne ikke hente gæt.</div> : null}
                            {guessesLoading ? <div className="kampdag-muted">Henter gæt...</div> : null}

                            {!guessesLoading && matchGuesses.length === 0 ? (
                              <div className="kampdag-muted">Der er ikke nogle gæt.</div>
                            ) : (
                              <div className="kampdag-guess-table-wrapper">
                                <table className="kampdag-guess-table">
                                  <thead>
                                    <tr>
                                      <th>Mate</th>
                                      {enabledCats.map((def) => (
                                        <th key={def.key}>{def.short || def.label}</th>
                                      ))}
                                      {selectedLeaderboard.hasScoring ? <th className="kampdag-points-col">Point</th> : null}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(selectedLeaderboard.hasScoring ? selectedLeaderboard.rows : matchGuesses.map((g) => ({ guess: g, perCategory: {}, totalPoints: 0, anyScored: false }))).map((row, idx) => {
                                      const g = row.guess
                                      const isLeader = selectedLeaderboard.hasScoring && row.totalPoints === selectedLeaderboard.leaderPoints && row.totalPoints > 0
                                      return (
                                        <tr key={g.id} className={isLeader ? 'kampdag-guess-row--leader' : ''}>
                                          <td className="kampdag-guess-name">
                                            {selectedLeaderboard.hasScoring ? (
                                              <span className="kampdag-medal" aria-hidden="true">{medalFor(idx)}</span>
                                            ) : null}
                                            {g.mateName || 'Ukendt'}
                                          </td>
                                          {enabledCats.map((def) => {
                                            const hit = guessHitsCategory(match, g, def)
                                            const cell = selectedLeaderboard.hasScoring ? row.perCategory[def.key] : null
                                            const cellPts = cell?.scored ? cell.points : null
                                            return (
                                              <td
                                                key={def.key}
                                                className={`kampdag-guess-cell${hit ? ' kampdag-guess-cell--hit' : ''}`}
                                                title={hit ? 'Ramte rigtigt' : undefined}
                                              >
                                                {renderGuessValue(g, def)}
                                                {hit ? <span aria-hidden="true"> ✓</span> : null}
                                                {cellPts !== null && cellPts > 0 && !hit ? (
                                                  <span className="kampdag-cell-points">+{cellPts}</span>
                                                ) : null}
                                              </td>
                                            )
                                          })}
                                          {selectedLeaderboard.hasScoring ? (
                                            <td className="kampdag-points-col">
                                              <strong>{row.totalPoints}</strong>
                                            </td>
                                          ) : null}
                                        </tr>
                                      )
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                      </Card>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <p className="kampdag-muted">Ingen kampdage.</p>
          )}
        </div>
      </div>

      <Modal
        isOpen={guessDialogOpen}
        onClose={closeGuessDialog}
        title="Gæt kampdag"
        size="small"
        className="kampdag-modal"
      >
        {selectedMatch ? (
          <>
            <div className="kampdag-modal-subtitle">
              {selectedMatch.title || `${selectedMatch.homeTeam} vs ${selectedMatch.awayTeam}`}
            </div>
            <div className="kampdag-muted" style={{ marginBottom: 12 }}>
              {fmtKickoff(selectedMatch.kickoff)}
              {selectedMatch.venue ? ` · ${selectedMatch.venue}` : ''}
            </div>

            {selectedMatch.enableResult && (
              <div className="kampdag-input-row">
                <label className="kampdag-field">
                  <div className="kampdag-field-label">{selectedMatch.homeTeam}</div>
                  <input
                    className="kampdag-input"
                    type="number"
                    min={0}
                    value={guessForm.homeScore}
                    onChange={(e) => setGuessForm((p) => ({ ...p, homeScore: e.target.value }))}
                  />
                </label>
                <label className="kampdag-field">
                  <div className="kampdag-field-label">{selectedMatch.awayTeam}</div>
                  <input
                    className="kampdag-input"
                    type="number"
                    min={0}
                    value={guessForm.awayScore}
                    onChange={(e) => setGuessForm((p) => ({ ...p, awayScore: e.target.value }))}
                  />
                </label>
              </div>
            )}

            {selectedMatch.enableYellowCards && (
              <label className="kampdag-field" style={{ marginTop: 12 }}>
                <div className="kampdag-field-label">Antal gule kort i alt</div>
                <input
                  className="kampdag-input"
                  type="number"
                  min={0}
                  value={guessForm.yellowCards}
                  onChange={(e) => setGuessForm((p) => ({ ...p, yellowCards: e.target.value }))}
                />
              </label>
            )}

            {selectedMatch.enableRedCards && (
              <label className="kampdag-field" style={{ marginTop: 12 }}>
                <div className="kampdag-field-label">Antal røde kort i alt</div>
                <input
                  className="kampdag-input"
                  type="number"
                  min={0}
                  value={guessForm.redCards}
                  onChange={(e) => setGuessForm((p) => ({ ...p, redCards: e.target.value }))}
                />
              </label>
            )}

            {selectedMatch.enableCorners && (
              <label className="kampdag-field" style={{ marginTop: 12 }}>
                <div className="kampdag-field-label">Antal hjørnespark i alt</div>
                <input
                  className="kampdag-input"
                  type="number"
                  min={0}
                  value={guessForm.corners}
                  onChange={(e) => setGuessForm((p) => ({ ...p, corners: e.target.value }))}
                />
              </label>
            )}

            <div className="kampdag-modal-actions">
              <Button title="Annuller" onClick={closeGuessDialog} variant="outlined" color="danger" />
              <Button title="Gem gæt" onClick={handleSaveGuess} color="confirmation" loading={savingGuess} loadingText="Gemmer..." />
            </div>
          </>
        ) : null}
      </Modal>

      <Modal
        isOpen={rulesOpen}
        onClose={() => setRulesOpen(false)}
        title="Regler for Kampdag"
        size="medium"
        className="kampdag-modal kampdag-rules-modal"
      >
        <div className="kampdag-rules-body">
          <p className="kampdag-rules-lead">
            En admin opretter en kamp og vælger hvilke kategorier der spilles på (fx resultat, gule kort, røde kort
            og hjørnespark). Du gætter før kampstart. Når admin har indtastet facit, kan du se point og hvem der lå
            bedst.
          </p>

          <h3 className="kampdag-rules-heading">Gæt og låsning</h3>
          <ul className="kampdag-rules-list">
            <li>Du kan kun gætte og rette gæt indtil kampens starttidspunkt.</li>
            <li>Gule kort, røde kort og hjørnespark er altid <strong>i alt for begge hold</strong> i kampen.</li>
            <li>Hvilke felter du skal udfylde, afhænger af hvad admin har slået til for netop den kamp.</li>
          </ul>

          <h3 className="kampdag-rules-heading">Point (pr. kategori)</h3>
          <p className="kampdag-rules-p">
            For hver kategori med facit tælles point ud fra alle mates gæt på én gang:
          </p>
          <ul className="kampdag-rules-list">
            <li>
              <strong>2 point</strong> hvis dit gæt rammer det faktiske resultat præcist.
            </li>
            <li>
              <strong>1 point</strong> hvis du er <strong>tættest på</strong> facit blandt alle der har gættet i den
              kategori. Flere kan dele 1 point, hvis de har samme mindste afstand til facit (og ingen har ramt
              præcist).
            </li>
            <li>
              <strong>0 point</strong> hvis du ikke er tættest på, eller hvis du ikke har et gyldigt gæt i kategorien.
            </li>
          </ul>
          <p className="kampdag-rules-example">
            Eksempel: Facit er 5 gule kort. Gæt 4, 2 og 1 giver afstande 1, 3 og 4 — den der gættede 4 får 1 point.
            Hvis facit i stedet er 0 gule og to personer har gættet 1, er de begge tættest på (afstand 1) og får hver
            1 point, medmindre nogen ramte 0 (så får kun den/de 2 point).
          </p>

          <h3 className="kampdag-rules-heading">Resultat (mål)</h3>
          <p className="kampdag-rules-p">
            Afstand måles som summen af fejl på hjemme- og udemål:{' '}
            <code className="kampdag-rules-code">|hjemme − facit hjemme| + |ude − facit ude|</code>. Den mindste
            afstand vinder 1 point (eller 2 ved præcis ram).
          </p>

          <h3 className="kampdag-rules-heading">Oversigt og rangering</h3>
          <ul className="kampdag-rules-list">
            <li>Åbn en kamp og tryk på kortet for at se alle gæt.</li>
            <li>Når det faktiske resultat er indtastet, sorteres listen efter point, og der vises medaljer for top tre.</li>
            <li>Den samlede vinder er den med flest point på tværs af alle kategorier i den kamp.</li>
          </ul>

          <div className="kampdag-modal-actions">
            <Button title="Luk" onClick={() => setRulesOpen(false)} color="confirmation" />
          </div>
        </div>
        <Spacer size="30px" />
      </Modal>
    </div>
    </PullToRefresh>
  )
}

export default Kampdag
